import { Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { EmailNotificationType, SmartStatementBucket } from '@anduin/api';
import { applicationDebounceTime, AtomicTypeMock, ignoreFalsy, validateEmail } from '@anduin/shared';
import { BehaviorSubject, Observable, Subject, combineLatest, mergeMap } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { CollectionsSettingsStep, CollectionsTrackService } from '../../tracking';

export interface CollectionsAdditionalRecipient {
  email: string;
  emailNotificationType: EmailNotificationType;
  selectedBuckets: SmartStatementBucket[];
}

export class CollectionsAdditionalRecipientMock extends AtomicTypeMock<CollectionsAdditionalRecipient> {
  protected _model = {
    email: 'em@il.com',
    emailNotificationType: EmailNotificationType.CC,
    selectedBuckets: [SmartStatementBucket.ZERO_TO_THIRTY],
  };
}

function validateBucketSelections(formGroup: UntypedFormGroup) {
  return !formGroup?.controls?.zeroToThirty?.value &&
    !formGroup?.controls?.thirtyOneToSixty?.value &&
    !formGroup?.controls?.sixtyOneToNinety?.value &&
    !formGroup?.controls?.ninetyOneToOneTwenty?.value &&
    !formGroup?.controls?.overOneTwenty?.value
    ? { noBucketsSelected: true }
    : null;
}

function validateNoDuplicateEmails(formArray: UntypedFormArray): ValidationErrors | null {
  const emails = formArray.controls.map((control) => `${control.get('email').value}`.toUpperCase());
  const uniqueEmails = new Set(emails);
  return emails.length !== uniqueEmails.size ? { duplicateEmails: true } : null;
}

@Component({
  selector: 'app-additional-recipients-input',
  templateUrl: './additional-recipients-input.component.html',
  styleUrls: ['./additional-recipients-input.component.scss'],
  standalone: false,
})
export class AdditionalRecipientsInputComponent implements OnInit, OnDestroy {
  EmailNotificationType = EmailNotificationType;

  @ViewChildren('emailInputs', { read: ElementRef }) emailInputs: QueryList<ElementRef>;

  private readonly _unsubscribe$: Subject<void> = new Subject<void>();
  private readonly _additionalRecipients$: BehaviorSubject<CollectionsAdditionalRecipient[]> = new BehaviorSubject<
    CollectionsAdditionalRecipient[]
  >([]);
  private readonly _clientPartnerEmailNotificationType$: BehaviorSubject<EmailNotificationType> =
    new BehaviorSubject<EmailNotificationType>(EmailNotificationType.CC);
  private readonly _clientPartnerSelectedBuckets$: BehaviorSubject<SmartStatementBucket[]> = new BehaviorSubject<SmartStatementBucket[]>(
    [],
  );
  private readonly _additionalRecipientsArrayGroup$: BehaviorSubject<UntypedFormGroup> = new BehaviorSubject<UntypedFormGroup>(
    new UntypedFormGroup({ additionalRecipients: new UntypedFormArray([]) }),
  );
  additionalRecipientsArrayGroup$: Observable<UntypedFormGroup> = this._additionalRecipientsArrayGroup$.asObservable();

  @Input()
  set additionalRecipients(value: CollectionsAdditionalRecipient[]) {
    this._additionalRecipients$.next(value);
  }

  @Input()
  set clientPartnerEmailNotificationType(value: EmailNotificationType) {
    this._clientPartnerEmailNotificationType$.next(value);
  }

  @Input()
  set clientPartnerSelectedBuckets(value: SmartStatementBucket[]) {
    this._clientPartnerSelectedBuckets$.next(value);
  }

  private _disabled = false;

  @Input()
  set disabled(value: boolean) {
    this._disabled = value;
    // Trigger form recreation to apply disabled state
    this._additionalRecipients$.next(this._additionalRecipients$.getValue());
    this._clientPartnerEmailNotificationType$.next(this._clientPartnerEmailNotificationType$.getValue());
    this._clientPartnerSelectedBuckets$.next(this._clientPartnerSelectedBuckets$.getValue());
  }

  get disabled(): boolean {
    return this._disabled;
  }

  @Output()
  isValid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()
  additionalRecipientsChange: EventEmitter<CollectionsAdditionalRecipient[]> = new EventEmitter<CollectionsAdditionalRecipient[]>();
  @Output()
  clientPartnerSelectedBucketsChange: EventEmitter<SmartStatementBucket[]> = new EventEmitter<SmartStatementBucket[]>();
  @Output()
  clientPartnerEmailNotificationTypeChange: EventEmitter<EmailNotificationType> = new EventEmitter<EmailNotificationType>();

  clientPartnerForm$: Observable<UntypedFormGroup> = combineLatest([
    this._clientPartnerEmailNotificationType$,
    this._clientPartnerSelectedBuckets$,
  ]).pipe(
    map(([emailNotificationType, selectedBuckets]) => {
      const form = AdditionalRecipientsInputComponent.createGroupForRow(emailNotificationType, selectedBuckets, false);
      if (this._disabled) {
        form.disable();
      }
      return form;
    }),
    shareReplay(1),
  );

  populateAdditionalRecipientsArrayGroup$: Observable<UntypedFormGroup> = this._additionalRecipients$.pipe(
    map(
      (additionalRecipients) =>
        new UntypedFormArray(
          additionalRecipients.map((additionalRecipient) =>
            AdditionalRecipientsInputComponent.createGroupForRow(
              additionalRecipient.emailNotificationType,
              additionalRecipient.selectedBuckets,
              true,
              additionalRecipient.email,
            ),
          ),
          validateNoDuplicateEmails,
        ),
    ),
    map((formArray) => {
      const form = new UntypedFormGroup({ additionalRecipients: formArray });
      if (this._disabled) {
        form.disable();
      }
      return form;
    }),
    tap((form) => this._additionalRecipientsArrayGroup$.next(form)),
    takeUntil(this._unsubscribe$),
  );

  isFormValid$: Observable<boolean> = this._additionalRecipientsArrayGroup$.pipe(
    switchMap((formGroup) =>
      formGroup.statusChanges.pipe(
        map((_) => formGroup.valid),
        distinctUntilChanged(),
        tap((isFormValid) => this.isValid.emit(isFormValid)),
      ),
    ),
    takeUntil(this._unsubscribe$),
  );

  updatedAdditionalRecipients$: Observable<CollectionsAdditionalRecipient[]> = this._additionalRecipientsArrayGroup$.pipe(
    mergeMap((formGroup) =>
      formGroup.valueChanges.pipe(
        applicationDebounceTime(),
        map((_) =>
          (formGroup.controls.additionalRecipients as UntypedFormArray)?.controls?.map((additionalRecipientForm: UntypedFormGroup) => ({
            email: additionalRecipientForm?.controls?.email?.value,
            emailNotificationType: additionalRecipientForm?.controls?.emailNotificationType?.value,
            selectedBuckets: [
              additionalRecipientForm?.controls?.zeroToThirty?.value ? SmartStatementBucket.ZERO_TO_THIRTY : undefined,
              additionalRecipientForm?.controls?.thirtyOneToSixty?.value ? SmartStatementBucket.THIRTY_ONE_TO_SIXTY : undefined,
              additionalRecipientForm?.controls?.sixtyOneToNinety?.value ? SmartStatementBucket.SIXTY_ONE_TO_NINETY : undefined,
              additionalRecipientForm?.controls?.ninetyOneToOneTwenty?.value ? SmartStatementBucket.NINETY_ONE_TO_ONE_TWENTY : undefined,
              additionalRecipientForm?.controls?.overOneTwenty?.value ? SmartStatementBucket.OVER_ONE_TWENTY : undefined,
            ].filter(Boolean),
          })),
        ),
        tap((updatedAdditionalRecipients) => this.additionalRecipientsChange.emit(updatedAdditionalRecipients)),
      ),
    ),
    takeUntil(this._unsubscribe$),
  );

  clientPartnerBucketsMonitor$: Observable<SmartStatementBucket[]> = this.clientPartnerForm$.pipe(
    ignoreFalsy(),
    switchMap((formGroup) =>
      formGroup.valueChanges.pipe(
        map((_) =>
          [
            formGroup?.controls?.zeroToThirty?.value ? SmartStatementBucket.ZERO_TO_THIRTY : undefined,
            formGroup?.controls?.thirtyOneToSixty?.value ? SmartStatementBucket.THIRTY_ONE_TO_SIXTY : undefined,
            formGroup?.controls?.sixtyOneToNinety?.value ? SmartStatementBucket.SIXTY_ONE_TO_NINETY : undefined,
            formGroup?.controls?.ninetyOneToOneTwenty?.value ? SmartStatementBucket.NINETY_ONE_TO_ONE_TWENTY : undefined,
            formGroup?.controls?.overOneTwenty?.value ? SmartStatementBucket.OVER_ONE_TWENTY : undefined,
          ].filter(Boolean),
        ),
        tap((buckets) => this.clientPartnerSelectedBucketsChange.emit(buckets)),
      ),
    ),
    takeUntil(this._unsubscribe$),
  );
  clientPartnerNotificationTypeMonitor$: Observable<EmailNotificationType> = this.clientPartnerForm$.pipe(
    ignoreFalsy(),
    switchMap((formGroup) =>
      formGroup.valueChanges.pipe(
        map((_) => formGroup?.controls?.emailNotificationType?.value),
        tap((emailNotificationType) => this.clientPartnerEmailNotificationTypeChange.emit(emailNotificationType)),
      ),
    ),
    takeUntil(this._unsubscribe$),
  );

  private readonly trackService = inject(CollectionsTrackService);

  ngOnInit(): void {
    this.populateAdditionalRecipientsArrayGroup$.subscribe();
    this.isFormValid$.subscribe();
    this.updatedAdditionalRecipients$.subscribe();
    this.clientPartnerBucketsMonitor$.subscribe();
    this.clientPartnerNotificationTypeMonitor$.subscribe();
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  addFormGroup(): void {
    const formGroup = this._additionalRecipientsArrayGroup$.value;
    const recipientsArray = formGroup.controls.additionalRecipients as UntypedFormArray;
    recipientsArray.push(AdditionalRecipientsInputComponent.createGroupForRow(undefined, undefined, true, undefined, true));

    this._additionalRecipientsArrayGroup$.next(formGroup);

    this.trackService.trackWorkflow(CollectionsSettingsStep.ADDITIONAL_RECIPIENT_ADDED, {
      recipientCount: recipientsArray.length,
    });

    // Need to wait for the next tick to get the correct number of email inputs
    setTimeout(() => this.emailInputs.last.nativeElement.focus(), 0);
  }

  removeAdditionalRecipient(index: number): void {
    const formGroup = this._additionalRecipientsArrayGroup$.value;
    const recipientsArray = formGroup.controls.additionalRecipients as UntypedFormArray;
    recipientsArray.removeAt(index);
    this._additionalRecipientsArrayGroup$.next(formGroup);

    this.trackService.trackWorkflow(CollectionsSettingsStep.ADDITIONAL_RECIPIENT_REMOVED, {
      recipientCount: recipientsArray.length,
    });
  }

  markAsAllAsTouched(): void {
    this._additionalRecipientsArrayGroup$.value.markAllAsTouched();
  }

  reset(
    additionalRecipients: CollectionsAdditionalRecipient[],
    clientPartnerNotificationType: EmailNotificationType,
    clientPartnerBuckets: SmartStatementBucket[],
  ): void {
    this.additionalRecipients = additionalRecipients;
    this.clientPartnerEmailNotificationType = clientPartnerNotificationType;
    this.clientPartnerSelectedBuckets = clientPartnerBuckets;
  }

  private static createGroupForRow(
    emailNotificationType: EmailNotificationType = EmailNotificationType.CC,
    selectedBuckets: SmartStatementBucket[] = [],
    includeEmailInput = false,
    email?: string,
    isNewRow = false,
  ): UntypedFormGroup {
    const buckets: SmartStatementBucket[] = selectedBuckets || [];

    const formGroup = new UntypedFormGroup(
      {
        emailNotificationType: new UntypedFormControl(emailNotificationType || EmailNotificationType.CC, Validators.required),
        zeroToThirty: new UntypedFormControl(buckets.includes(SmartStatementBucket.ZERO_TO_THIRTY) || isNewRow),
        thirtyOneToSixty: new UntypedFormControl(buckets.includes(SmartStatementBucket.THIRTY_ONE_TO_SIXTY) || isNewRow),
        sixtyOneToNinety: new UntypedFormControl(buckets.includes(SmartStatementBucket.SIXTY_ONE_TO_NINETY) || isNewRow),
        ninetyOneToOneTwenty: new UntypedFormControl(buckets.includes(SmartStatementBucket.NINETY_ONE_TO_ONE_TWENTY) || isNewRow),
        overOneTwenty: new UntypedFormControl(buckets.includes(SmartStatementBucket.OVER_ONE_TWENTY) || isNewRow),
      },
      {
        validators: [validateBucketSelections],
        updateOn: `change`,
      },
    );

    if (includeEmailInput) {
      formGroup.addControl('email', new UntypedFormControl(email || '', [Validators.required, validateEmail]));
    }

    return formGroup;
  }
}
